import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { bandLabel, type AudienceBand } from "@shared/plan-matrix";
import { PageHeader } from "../../components/PageHeader";
import {
  foyerApi,
  type CustomerPick,
  type CustomerPlan,
} from "../../http/foyer";
import { tokens } from "../../theme";
import { describeError, useToast } from "../../components/toast";
import { OwnerGate } from "../../components/supplier/OwnerGate";
import { useFormat } from "../../i18n/format";

/**
 * One customer organisation (Sigge 2026-10-07: "make the customer clickable
 * and then with thumbnails showing what movies they have selected and add a
 * trash button so an admin can revoke rights for a movie").
 *
 * Its subscription, then every film it picked as a thumbnail. The trash
 * button revokes a film after a confirmation that says what happens: the
 * organisation's viewers lose it at once, and the pick is freed for another
 * film. The revocation is kept on record (who, when).
 */

export default function SupplierCustomerPage() {
  const toast = useToast();
  const { t, i18n } = useTranslation(["supplier", "common"]);
  const fmt = useFormat();
  const lang = i18n.language === "sv" ? "sv" : "en";
  const sek = (n: number) =>
    fmt.currency(n, "SEK", { maximumFractionDigits: n % 1 === 0 ? 0 : 2 });
  const { accountId = "" } = useParams();
  const [plan, setPlan] = useState<CustomerPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<CustomerPick | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setPlan(await foyerApi.customerPlan(accountId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [accountId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke(pick: CustomerPick) {
    setBusy(true);
    setError(null);
    try {
      await foyerApi.revokeCustomerPick(accountId, pick.assetId);
      toast.success(
        t("supplierCustomerPage.revoked", {
          title: pick.title,
          customer: plan?.account.name ?? t("supplierCustomerPage.theCustomer"),
        })
      );
      await load();
    } catch (e) {
      toast.error(
        describeError(t, "revokeCustomerPick", e, { title: pick.title })
      );
    } finally {
      setBusy(false);
      setRevoking(null);
    }
  }

  if (error && !plan) return <Alert severity="error">{error}</Alert>;
  if (!plan) return <CircularProgress size={24} />;
  const sub = plan.subscription;

  return (
    <Stack spacing={3} sx={{ maxWidth: 1100 }}>
      <Box>
        <Button component={RouterLink} to="/supplier/customers" size="small">
          {t("supplierCustomerPage.back")}
        </Button>
      </Box>
      <PageHeader
        eyebrow={t("supplierCustomerPage.eyebrow")}
        title={plan.account.name}
        subtitle={
          plan.account.billingRef
            ? t("supplierCustomerPage.orgNo", {
                orgNumber: plan.account.billingRef,
              })
            : undefined
        }
      />
      {error && <Alert severity="error">{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {t("supplierCustomerPage.subscription")}
        </Typography>
        {sub ? (
          <Typography variant="body2" data-testid="customer-subscription">
            {t("supplierCustomerPage.subscriptionText", {
              plan: sub.label,
              films:
                sub.films === null
                  ? t("supplierCustomerPage.fullLibrary")
                  : t("supplierCustomerPage.films", { count: sub.films }),
              band: bandLabel(sub.band as AudienceBand, lang),
              price: sek(sub.yearlyPrice),
              date: fmt.date(sub.renewsAt),
            })}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            {t("supplierCustomerPage.noSubscription")}
          </Typography>
        )}
      </Paper>

      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {sub?.films
            ? t("supplierCustomerPage.pickedOf", {
                count: plan.picks.length,
                max: sub.films,
              })
            : t("supplierCustomerPage.picked", { count: plan.picks.length })}
        </Typography>
        {sub?.films === null ? (
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            {t("supplierCustomerPage.fullLibraryNoPicks")}
          </Typography>
        ) : plan.picks.length === 0 ? (
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            {t("supplierCustomerPage.noPicks")}
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 2,
            }}
          >
            {plan.picks.map((p) => (
              <Paper
                key={p.assetId}
                variant="outlined"
                sx={{ overflow: "hidden" }}
                data-testid={`customer-pick-${p.assetId}`}
              >
                <Box
                  sx={{
                    aspectRatio: "16 / 9",
                    bgcolor: tokens.rowAlt,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {p.image ? (
                    <Box
                      component="img"
                      src={p.image}
                      alt=""
                      loading="lazy"
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography
                      sx={{ typography: "display", color: tokens.inkMuted }}
                    >
                      {p.title.trim().charAt(0)}
                    </Typography>
                  )}
                </Box>
                <Stack
                  direction="row"
                  alignItems="flex-start"
                  sx={{ p: 1.25 }}
                  spacing={1}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "fontWeightSemibold" }}
                      noWrap
                      title={p.title}
                    >
                      {p.title}
                    </Typography>
                    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                      {p.pickedBy
                        ? t("supplierCustomerPage.pickedOnBy", {
                            date: fmt.date(p.pickedAt),
                            by: p.pickedBy,
                          })
                        : t("supplierCustomerPage.pickedOn", {
                            date: fmt.date(p.pickedAt),
                          })}
                    </Typography>
                  </Box>
                  <OwnerGate resource="storefront" level="edit">
                    <IconButton
                      size="small"
                      aria-label={t("supplierCustomerPage.revokeAria", {
                        title: p.title,
                      })}
                      onClick={() => setRevoking(p)}
                      disabled={busy}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </OwnerGate>
                </Stack>
              </Paper>
            ))}
          </Box>
        )}
      </Box>

      <Dialog
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("supplierCustomerPage.revokeTitle", { title: revoking?.title })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t("supplierCustomerPage.revokeBody", {
              customer: plan.account.name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoking(null)}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            onClick={() => revoking && void revoke(revoking)}
          >
            {t("supplierCustomerPage.revoke")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
